import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import PublishIcon from '@mui/icons-material/Publish';
import ArchiveIcon from '@mui/icons-material/Archive';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import useKnowledgeBase from '../../hooks/useKnowledgeBase';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Alle' },
  { value: 'draft', label: 'Entwurf' },
  { value: 'published', label: 'Veröffentlicht' },
  { value: 'archived', label: 'Archiviert' },
];

const ARTICLE_STATUS_OPTIONS = [
  { value: 'draft', label: 'Entwurf' },
  { value: 'published', label: 'Veröffentlicht' },
  { value: 'archived', label: 'Archiviert' },
];

const PAGE_SIZE = 20;

function formatDate(isoString) {
  if (!isoString) return '—';
  return dayjs(isoString).format('DD.MM.YYYY HH:mm');
}

function statusColor(status) {
  switch (status) {
    case 'published':
      return 'success';
    case 'archived':
      return 'default';
    default:
      return 'warning';
  }
}

function KnowledgeBaseManagement() {
  const authCtx = useContext(AuthContext);
  const {
    listArticles,
    getArticle,
    createArticle,
    updateArticle,
    publishArticle,
    archiveArticle,
    deleteArticle,
    generateArticleFromUrl,
    generateTags,
  } = useKnowledgeBase();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [articles, setArticles] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogState, setDialogState] = useState({ open: false, mode: 'create', articleId: null });
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [dialogArticle, setDialogArticle] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [savingArticle, setSavingArticle] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const dialogAudience = 'external';
  const [isGeneratingFromUrl, setIsGeneratingFromUrl] = useState(false);
  const [isGeneratingTags, setIsGeneratingTags] = useState(false);

  const searchRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const titleRef = useRef(null);
  const tagsRef = useRef(null);
  const contentRef = useRef(null);
  const articleStatusRef = useRef(null);
  const sourceUrlRef = useRef(null);

  const canEdit = authCtx.checkRight('knowledgeBase:update');
  const canDelete = authCtx.checkRight('knowledgeBase:delete');

  const fetchArticles = useCallback(
    async ({ status, page: pageOverride, search } = {}) => {
      const nextStatus = typeof status === 'string' ? status : statusFilter;
      const nextSearch = typeof search === 'string' ? search : searchTerm;
      const nextPage = typeof pageOverride === 'number' ? pageOverride : page;
      setIsFetching(true);
      try {
        const response = await listArticles({
          status: nextStatus,
          search: nextSearch,
          limit: PAGE_SIZE,
          skip: nextPage * PAGE_SIZE,
        });
        setArticles(response?.items || []);
        const responseCount = Number.isFinite(response?.count)
          ? response.count
          : response?.items?.length || 0;
        setTotalCount(responseCount);
        if (typeof pageOverride === 'number') {
          setPage(pageOverride);
        }
      } catch (error) {
        toast.error('Fehler beim Laden der Knowledge-Base-Artikel');
      } finally {
        setIsFetching(false);
      }
    },
    [listArticles, page, searchTerm, statusFilter]
  );

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    fetchArticles();
  }, [fetchArticles]);

  const handleSearchSubmit = useCallback(
    event => {
      event.preventDefault();
      const value = searchRef.current ? searchRef.current.value.trim() : '';
      setSearchTerm(value);
      setPage(0);
      fetchArticles({ search: value, page: 0 });
    },
    [fetchArticles]
  );

  const handleStatusChange = useCallback(
    event => {
      const nextStatus = event.target.value;
      setStatusFilter(nextStatus);
      setPage(0);
      fetchArticles({ status: nextStatus, page: 0 });
    },
    [fetchArticles]
  );

  const closeDialog = useCallback(() => {
    setDialogState({ open: false, mode: 'create', articleId: null });
    setDialogArticle(null);
    setDialogLoading(false);
    setSavingArticle(false);
    setIsGeneratingFromUrl(false);
    setIsGeneratingTags(false);
    if (titleRef.current) titleRef.current.value = '';
    if (tagsRef.current) tagsRef.current.value = '';
    if (contentRef.current) contentRef.current.value = '';
    if (articleStatusRef.current) articleStatusRef.current.value = 'draft';
    if (sourceUrlRef.current) sourceUrlRef.current.value = '';
  }, []);

  const openCreateDialog = useCallback(() => {
    setDialogState({ open: true, mode: 'create', articleId: null });
    setDialogArticle(null);
    if (articleStatusRef.current) {
      articleStatusRef.current.value = 'draft';
    }
  }, []);

  const openEditDialog = useCallback(
    articleId => {
      setDialogState({ open: true, mode: 'edit', articleId });
      setDialogLoading(true);
      getArticle(articleId)
        .then(article => {
          setDialogArticle(article);
        })
        .catch(() => {
          toast.error('Fehler beim Laden des Artikels');
          closeDialog();
        })
        .finally(() => {
          setDialogLoading(false);
        });
    },
    [closeDialog, getArticle]
  );

  useEffect(() => {
    if (!dialogState.open) return;
    const defaults = dialogArticle || {};
    if (titleRef.current) titleRef.current.value = defaults.title || '';
    if (tagsRef.current)
      tagsRef.current.value = Array.isArray(defaults.tags) ? defaults.tags.join(', ') : '';
    if (contentRef.current) contentRef.current.value = defaults.contentMarkdown || '';
    if (articleStatusRef.current) {
      articleStatusRef.current.value = defaults.status || 'draft';
    }
  }, [dialogArticle, dialogState.open]);

  const parseTags = value => {
    if (!value) return [];
    return value
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);
  };

  const handleSaveArticle = useCallback(async () => {
    if (!titleRef.current || !contentRef.current) {
      toast.error('Titel und Inhalt sind erforderlich');
      return;
    }

    const rawTitle = titleRef.current.value.trim();
    const rawContent = contentRef.current.value.trim();
    if (!rawTitle || !rawContent) {
      toast.error('Titel und Inhalt sind erforderlich');
      return;
    }

    const payload = {
      title: rawTitle,
      tags: parseTags(tagsRef.current ? tagsRef.current.value : ''),
      contentMarkdown: rawContent,
      status: articleStatusRef.current ? articleStatusRef.current.value : 'draft',
    };

    setSavingArticle(true);
    try {
      if (dialogState.mode === 'create') {
        await createArticle(payload);
        toast.success('Artikel erstellt');
      } else if (dialogState.articleId) {
        await updateArticle(dialogState.articleId, payload);
        toast.success('Artikel aktualisiert');
      }
      closeDialog();
      if (dialogState.mode === 'create') {
        await fetchArticles({ page: 0 });
      } else {
        await fetchArticles();
      }
    } catch (error) {
      toast.error('Fehler beim Speichern des Artikels');
      setSavingArticle(false);
    }
  }, [createArticle, dialogState, updateArticle, closeDialog, fetchArticles]);

  const handlePublish = useCallback(
    async articleId => {
      setProcessingAction(true);
      try {
        await publishArticle(articleId);
        toast.success('Artikel veröffentlicht');
        fetchArticles();
      } catch (error) {
        toast.error('Fehler beim Veröffentlichen');
      } finally {
        setProcessingAction(false);
      }
    },
    [fetchArticles, publishArticle]
  );

  const handleArchive = useCallback(
    async articleId => {
      setProcessingAction(true);
      try {
        await archiveArticle(articleId);
        toast.success('Artikel archiviert');
        fetchArticles();
      } catch (error) {
        toast.error('Fehler beim Archivieren');
      } finally {
        setProcessingAction(false);
      }
    },
    [archiveArticle, fetchArticles]
  );

  const handleGenerateFromUrl = useCallback(async () => {
    const url = sourceUrlRef.current ? sourceUrlRef.current.value.trim() : '';
    if (!url || url.length < 5) {
      toast.error('Bitte eine gültige URL angeben');
      return;
    }
    setIsGeneratingFromUrl(true);
    try {
      const response = await generateArticleFromUrl({
        url,
      });
      if (!response || !response.contentMarkdown || !response.title) {
        toast.error('Fehler beim Generieren des Artikels aus URL');
        return;
      }
      if (titleRef.current) titleRef.current.value = response.title || '';
      if (contentRef.current) contentRef.current.value = response.contentMarkdown || '';
      if (tagsRef.current && Array.isArray(response.tags)) {
        tagsRef.current.value = response.tags.join(', ');
      }
      toast.success('Artikel aus URL generiert');
    } catch (error) {
      toast.error('Fehler beim Generieren des Artikels aus URL');
      throw error;
    } finally {
      setIsGeneratingFromUrl(false);
    }
  }, [generateArticleFromUrl]);

  const handleGenerateTags = useCallback(async () => {
    const title = titleRef.current ? titleRef.current.value.trim() : '';
    const contentMarkdown = contentRef.current ? contentRef.current.value.trim() : '';
    if (!contentMarkdown || contentMarkdown.length < 20) {
      toast.error('Bitte zuerst einen Inhalt angeben');
      return;
    }
    setIsGeneratingTags(true);
    try {
      const response = await generateTags({ title, contentMarkdown });
      const tags = Array.isArray(response?.tags) ? response.tags : [];
      if (!tags.length) {
        toast.error('Keine Tags gefunden');
        return;
      }
      if (tagsRef.current) {
        tagsRef.current.value = tags.join(', ');
      }
      toast.success('Tags generiert');
    } catch (error) {
      toast.error('Fehler beim Generieren der Tags');
      throw error;
    } finally {
      setIsGeneratingTags(false);
    }
  }, [generateTags]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setProcessingAction(true);
    try {
      await deleteArticle(deleteTarget._id);
      toast.success('Artikel gelöscht');
      setDeleteTarget(null);
      const nextTotal = totalCount > 0 ? totalCount - 1 : 0;
      const maxPage = Math.max(0, Math.ceil(nextTotal / PAGE_SIZE) - 1);
      const nextPage = Math.min(page, maxPage);
      if (nextPage !== page) {
        setPage(nextPage);
      }
      await fetchArticles({ page: nextPage });
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setProcessingAction(false);
    }
  }, [deleteArticle, deleteTarget, fetchArticles, page, totalCount]);

  const handlePageChange = useCallback(
    (event, newPage) => {
      setPage(newPage);
      fetchArticles({ page: newPage });
    },
    [fetchArticles]
  );

  const articleCount = totalCount || articles.length;

  if (!authCtx.checkRight('knowledgeBase:read')) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box sx={{ padding: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Box>
          <Typography variant='body2' color='text.secondary'>
            {articleCount} Artikel verfügbar
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          width={{ xs: '100%', md: 'auto' }}
        >
          <Box component='form' onSubmit={handleSearchSubmit} sx={{ width: '100%' }}>
            <TextField
              fullWidth
              inputRef={searchRef}
              placeholder='Suche nach Titel oder Inhalt'
              variant='outlined'
              size='small'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <SearchIcon fontSize='small' color='action' />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            />
          </Box>
          <TextField
            select
            size='small'
            value={statusFilter}
            onChange={handleStatusChange}
            sx={{
              minWidth: isMobile ? '100%' : 180,
              width: isMobile ? '100%' : 'auto',
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.background.paper,
              },
            }}
          >
            {STATUS_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {canEdit && (
            <Button
              variant='contained'
              color='success'
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Neu
            </Button>
          )}
        </Stack>
      </Stack>

      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Titel</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>Bearbeitet</TableCell>
                <TableCell>Veröffentlicht</TableCell>
                <TableCell align='right'>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching && (
                <TableRow>
                  <TableCell colSpan={8} align='center'>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              )}
              {!isFetching && articles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align='center'>
                    <Typography variant='body2' color='text.secondary'>
                      Keine Artikel gefunden.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!isFetching &&
                articles.map(article => (
                  <TableRow key={article._id}>
                    <TableCell>
                      <Typography variant='subtitle2'>{article.title}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          ARTICLE_STATUS_OPTIONS.find(option => option.value === article.status)
                            ?.label || article.status
                        }
                        color={statusColor(article.status)}
                        size='small'
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction='row' gap={0.5} flexWrap='wrap'>
                        {Array.isArray(article.tags) && article.tags.length > 0
                          ? article.tags.map(tag => (
                              <Chip
                                key={tag}
                                label={tag}
                                size='small'
                                variant='outlined'
                                sx={{ mb: 0.5 }}
                              />
                            ))
                          : '—'}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>
                        {formatDate(article.meta?.editedAt || article.meta?.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>
                        {formatDate(article.meta?.publishedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={1}
                        justifyContent='flex-end'
                        alignItems={{ xs: 'stretch', lg: 'center' }}
                      >
                        {canEdit && (
                          <Tooltip title='Bearbeiten'>
                            <span>
                              <Button
                                size='small'
                                variant='outlined'
                                startIcon={<EditIcon />}
                                onClick={() => openEditDialog(article._id)}
                                sx={{ width: { xs: '100%', lg: 'auto' } }}
                              >
                                Bearbeiten
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {canEdit && article.status !== 'published' && (
                          <Tooltip title='Veröffentlichen'>
                            <span>
                              <Button
                                size='small'
                                variant='outlined'
                                startIcon={<PublishIcon />}
                                onClick={() => handlePublish(article._id)}
                                disabled={processingAction}
                                sx={{ width: { xs: '100%', lg: 'auto' } }}
                              >
                                Publish
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {canEdit && article.status !== 'archived' && (
                          <Tooltip title='Archivieren'>
                            <span>
                              <Button
                                size='small'
                                variant='outlined'
                                startIcon={<ArchiveIcon />}
                                onClick={() => handleArchive(article._id)}
                                disabled={processingAction}
                                sx={{ width: { xs: '100%', lg: 'auto' } }}
                              >
                                Archiv
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title='Löschen'>
                            <span>
                              <Button
                                size='small'
                                variant='outlined'
                                color='error'
                                startIcon={<DeleteIcon />}
                                onClick={() => setDeleteTarget(article)}
                                disabled={processingAction}
                                sx={{ width: { xs: '100%', lg: 'auto' } }}
                              >
                                Löschen
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component='div'
          count={totalCount || 0}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={PAGE_SIZE}
          onRowsPerPageChange={() => {}}
          rowsPerPageOptions={[]}
          labelRowsPerPage=''
          showFirstButton
          showLastButton
        />
      </Paper>

      <Dialog open={dialogState.open} onClose={closeDialog} fullWidth maxWidth='md'>
        <DialogTitle>
          {dialogState.mode === 'create'
            ? 'Neuen Knowledge-Base-Artikel erstellen'
            : 'Knowledge-Base-Artikel bearbeiten'}
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {dialogLoading ? (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems='center'>
                <TextField
                  label='Quelle (URL)'
                  fullWidth
                  inputRef={sourceUrlRef}
                  placeholder='https://...'
                  disabled={isGeneratingFromUrl || savingArticle}
                />
                <Button
                  variant='contained'
                  onClick={handleGenerateFromUrl}
                  disabled={isGeneratingFromUrl || savingArticle}
                  sx={{ minWidth: 220, width: { xs: '100%', md: 'auto' } }}
                >
                  {isGeneratingFromUrl ? (
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <CircularProgress size={18} color='inherit' />
                      <span>Generiere...</span>
                    </Stack>
                  ) : (
                    'Aus URL generieren'
                  )}
                </Button>
              </Stack>
              <Box
                component='input'
                type='text'
                placeholder='Titel'
                ref={titleRef}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.23)',
                  background: 'transparent',
                  color: 'inherit',
                }}
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box
                  component='input'
                  type='text'
                  placeholder='Tags (kommagetrennt)'
                  ref={tagsRef}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.23)',
                    background: 'transparent',
                    color: 'inherit',
                  }}
                />
                <Button
                  variant='outlined'
                  onClick={handleGenerateTags}
                  disabled={isGeneratingTags || savingArticle || dialogLoading}
                  sx={{ width: { xs: '100%', md: 'auto' }, minWidth: 180 }}
                >
                  {isGeneratingTags ? (
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <CircularProgress size={18} color='inherit' />
                      <span>Tags...</span>
                    </Stack>
                  ) : (
                    'Tags generieren'
                  )}
                </Button>
              </Stack>
              <Box
                component='select'
                ref={articleStatusRef}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 8,
                  border: `1px solid ${theme.palette.divider}`,
                  background: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                }}
              >
                {ARTICLE_STATUS_OPTIONS.map(option => (
                  <option
                    key={option.value}
                    value={option.value}
                    style={{
                      color: theme.palette.text.primary,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    {option.label}
                  </option>
                ))}
              </Box>
              <Box
                component='textarea'
                ref={contentRef}
                placeholder='Markdown-Inhalt'
                style={{
                  width: '100%',
                  minHeight: 240,
                  padding: '12px',
                  borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.23)',
                  background: 'transparent',
                  color: 'inherit',
                  resize: 'vertical',
                  fontFamily: 'monospace',
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={savingArticle}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            startIcon={<SaveIcon />}
            onClick={handleSaveArticle}
            disabled={savingArticle || dialogLoading || isGeneratingFromUrl || isGeneratingTags}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Artikel löschen</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Soll der Artikel{' '}
            <Typography component='span' color='primary.light' fontWeight={600}>
              {deleteTarget?.title}
            </Typography>{' '}
            endgültig gelöscht werden?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={processingAction}>
            Abbrechen
          </Button>
          <Button
            color='error'
            variant='contained'
            startIcon={<DeleteIcon />}
            onClick={handleConfirmDelete}
            disabled={processingAction}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default KnowledgeBaseManagement;
